import { Typography } from '@mui/material'
import { useGetMany, useListContext, useTranslate } from 'react-admin'

import { GROUP_MEMBERSHIPS } from '../config/groupMemberships'
import { SYSTEM_PERMISSION_READ } from '../config/permissions'
import { useResourcePermissions } from '../domain/permissions'

const GroupAccessSummary = ({ groupIds, resource }) => {
  const { linkedReference, linkedGroupSource, memberSource, memberCountKey, memberCountVariable } =
    GROUP_MEMBERSHIPS[resource]
  const translate = useTranslate()
  const { data: groups = [], isPending: groupsPending, error: groupsError } = useListContext()
  const [canReadLinkedGroups] = useResourcePermissions(linkedReference, SYSTEM_PERMISSION_READ)
  const groupsById = new Map(groups.map((group) => [String(group.id), group]))
  const hasCompleteGroups = groupIds.every((id) => Array.isArray(groupsById.get(String(id))?.[linkedGroupSource]))
  const linkedGroupIds = [
    ...new Set(groupIds.flatMap((id) => groupsById.get(String(id))?.[linkedGroupSource] ?? []).map(String)),
  ]
  const {
    data: linkedGroups = [],
    isPending: linkedGroupsPending,
    error: linkedGroupsError,
  } = useGetMany(
    linkedReference,
    { ids: linkedGroupIds },
    { enabled: canReadLinkedGroups && hasCompleteGroups && linkedGroupIds.length > 0, retry: false },
  )
  const linkedGroupsById = new Map(linkedGroups.map((group) => [String(group.id), group]))
  const hasCompleteMembers = linkedGroupIds.every((id) => Array.isArray(linkedGroupsById.get(id)?.[memberSource]))
  const isLoading =
    (groupIds.length > 0 && groupsPending) ||
    (canReadLinkedGroups && hasCompleteGroups && linkedGroupIds.length > 0 && linkedGroupsPending)

  if (isLoading) return null

  const hasMemberCount =
    !groupsError &&
    hasCompleteGroups &&
    (linkedGroupIds.length === 0 || (canReadLinkedGroups && !linkedGroupsError && hasCompleteMembers))
  const memberCount = new Set(
    linkedGroupIds.flatMap((id) => linkedGroupsById.get(id)?.[memberSource] ?? []).map(String),
  ).size

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {translate(
        `resources.${resource}.show.${hasMemberCount ? 'membershipSummary' : 'membershipSummaryUnavailable'}`,
        {
          groups: translate(`resources.${resource}.show.groupCount`, { smart_count: groupIds.length }),
          [memberCountVariable]: translate(memberCountKey, { smart_count: memberCount }),
        },
      )}
    </Typography>
  )
}

export default GroupAccessSummary
